/**
 * AccessPassCard.jsx — Diplomexa AccessPass premium QR card
 * ----------------------------------------------------------
 * A reusable navy/gold pass card. Renders the QR (using `qrcode.react`)
 * + person details + access scope. Used on:
 *  - MyAccessPassPage (delegate/visitor self-view)
 *  - VisitorRegisterPage (post-registration confirmation)
 *  - Admin pass-detail modal
 *
 * Props:
 *   pass     — the public pass DTO (id, full_name, role, event_title, …)
 *   qrToken  — the scannable signed token (string)
 *   compact  — render a smaller variant (for lists)
 */
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, Calendar, MapPin, Building2, IdCard } from "lucide-react";

const ROLE_LABEL = {
  delegate: "Delegate",
  chair: "Chair",
  co_chair: "Co-Chair",
  rapporteur: "Rapporteur",
  faculty_advisor: "Faculty Advisor",
  parent: "Parent / Guardian",
  observer: "Observer",
  visitor: "Visitor",
  guest: "Guest",
  speaker: "Speaker",
  sponsor: "Sponsor",
  partner: "Partner",
  volunteer: "Volunteer",
  press: "Press / Media",
  vip: "VIP / Chief Guest",
  staff: "Staff / Operations",
  trainer: "Trainer / Mentor",
  judge: "Judge / Evaluator",
};

function StatusBadge({ status }) {
  const map = {
    active:  { label: "Active",   cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
    pending: { label: "Pending Approval", cls: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
    revoked: { label: "Revoked",  cls: "bg-rose-500/15 text-rose-700 border-rose-500/30" },
    expired: { label: "Expired",  cls: "bg-slate-500/15 text-slate-600 border-slate-500/30" },
  };
  const s = map[status] || { label: status, cls: "bg-slate-500/15 text-slate-700 border-slate-500/30" };
  return (
    <span
      data-testid={`pass-status-${status}`}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full border px-2 py-0.5 ${s.cls}`}
    >
      <ShieldCheck className="h-3 w-3" />
      {s.label}
    </span>
  );
}

export default function AccessPassCard({ pass, qrToken, compact = false }) {
  if (!pass) return null;
  const role = (pass.role || pass.pass_type || "visitor").toLowerCase();
  const roleLabel = ROLE_LABEL[role] || role;
  const showQr = qrToken && pass.status === "active";
  const photoSrc = pass.photo_url
    ? (pass.photo_url.startsWith("http")
        ? pass.photo_url
        : `${process.env.REACT_APP_BACKEND_URL}${pass.photo_url}`)
    : null;

  return (
    <div
      data-testid="access-pass-card"
      className={`relative overflow-hidden rounded-md shadow-pass-lift bg-gradient-to-br from-brand-navy-dark via-brand-navy to-brand-navy-dark text-white wallet-enter ${
        compact ? "max-w-md" : "max-w-xl"
      }`}
    >
      {/* Apple-Wallet foil stripe — gold gradient hairline up top */}
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
      {/* Subtle parchment noise */}
      <div className="bg-noise absolute inset-0 pointer-events-none" />
      {/* Soft gold halos */}
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-brand-gold/15 blur-3xl" />
      <div className="absolute -bottom-20 -left-16 w-56 h-56 rounded-full bg-brand-gold/8 blur-3xl" />
      {/* Inner hairline gold ring */}
      <div className="absolute inset-3 rounded-md border border-brand-gold/15 pointer-events-none" />

      <div className="relative px-7 pt-7 pb-4 border-b border-brand-gold/15">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow text-brand-gold">
              Diplomexa · AccessPass
            </p>
            <h2 className="mt-2 font-display text-3xl leading-tight text-brand-champagne">
              {pass.event_title || "Conference Access"}
            </h2>
          </div>
          <StatusBadge status={pass.status} />
        </div>
      </div>

      <div className={`relative px-7 py-6 grid grid-cols-1 ${
        photoSrc ? "sm:grid-cols-[auto_1fr_auto]" : "sm:grid-cols-[1fr_auto]"
      } gap-6 items-center`}>
        {photoSrc && (
          <img
            src={photoSrc}
            alt={pass.full_name}
            className={`rounded-sm border border-brand-gold/40 object-cover shadow-gold-soft ${
              compact ? "h-20 w-20" : "h-24 w-24"
            }`}
            data-testid="pass-photo"
          />
        )}
        <div>
          <p className="eyebrow text-brand-gold/80">
            {roleLabel}
          </p>
          <p
            className={`mt-2 font-display ${compact ? "text-2xl" : "text-3xl"} leading-tight text-brand-champagne`}
            data-testid="pass-full-name"
          >
            {pass.full_name}
          </p>
          {pass.organisation && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-brand-champagne/70">
              <Building2 className="h-3.5 w-3.5 text-brand-gold" />
              {pass.organisation}
            </p>
          )}
          <div className="mt-4 space-y-1.5 text-xs text-brand-champagne/65">
            {(pass.valid_from || pass.valid_until) && (
              <p className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-brand-gold" />
                Valid{" "}
                {pass.valid_from
                  ? new Date(pass.valid_from).toLocaleDateString()
                  : "—"}
                {" → "}
                {pass.valid_until
                  ? new Date(pass.valid_until).toLocaleDateString()
                  : "—"}
              </p>
            )}
            {pass.day_session && (
              <p className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-brand-gold" />
                {pass.day_session}
              </p>
            )}
            {pass.access_level && (
              <p className="flex items-center gap-1.5 uppercase tracking-[0.18em] text-[10px] font-semibold text-brand-gold">
                <IdCard className="h-3.5 w-3.5" />
                {pass.access_level.replace(/_/g, " ")}
              </p>
            )}
          </div>
        </div>

        <div
          className="flex items-center justify-center rounded-sm bg-white p-3.5 shadow-[0_8px_28px_rgba(0,0,0,0.45)] ring-1 ring-brand-gold/30"
          data-testid="pass-qr-block"
        >
          {showQr ? (
            <QRCodeSVG
              value={qrToken}
              size={compact ? 120 : 168}
              level="M"
              includeMargin={false}
              fgColor="#0A192F"
              bgColor="#FFFFFF"
              data-testid="pass-qr-svg"
            />
          ) : (
            <div
              className={`flex flex-col items-center justify-center text-center text-slate-500 ${
                compact ? "w-[120px] h-[120px]" : "w-[168px] h-[168px]"
              }`}
              data-testid="pass-qr-placeholder"
            >
              <ShieldCheck className="h-8 w-8 text-slate-300" />
              <p className="mt-2 eyebrow text-slate-400">
                {pass.status === "pending"
                  ? "Awaiting approval"
                  : "QR unavailable"}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="relative px-7 pb-5 pt-3 border-t border-brand-gold/15 flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-brand-champagne/45">
        <span data-testid="pass-id-fragment" className="font-mono">
          ID · {(pass.id || "").slice(-10).toUpperCase()}
        </span>
        <span className="text-brand-gold">Signed · Enterprise security</span>
      </div>
    </div>
  );
}
